import type { User, LoginCredentials, CreateUserData, UpdateUserData, ChangePasswordData } from '../types';
import { PublicClientApplication } from '@azure/msal-browser';
import { MicrosoftGraphService } from './microsoftGraphService';
import { AZURE_GROUPS, AUTHORIZED_DOMAINS } from '../config/msalConfig';
import { supabase } from './supabase';
import { logger } from '../utils/logger';

// Sistema de autenticação baseado em SSO Microsoft Entra ID
// Não há mais dados mockados - apenas autenticação via Microsoft

export class AuthService {
  private static currentUser: User | null = null;
  public static msalInstance: PublicClientApplication | null = null;
  private static graphService: MicrosoftGraphService | null = null;
  private static useSSO: boolean = false;

  // Inicializar MSAL
  static initializeMSAL(msalInstance: PublicClientApplication) {
    // Evitar reinicialização se já existe uma instância
    if (this.msalInstance && this.msalInstance === msalInstance) {
      logger.debug('MSAL já inicializado, reutilizando instância');
      return;
    }

    this.msalInstance = msalInstance;
    this.graphService = new MicrosoftGraphService(msalInstance);
    this.useSSO = true;
    logger.success('MSAL inicializado com sucesso');
  }

  // Habilitar/desabilitar SSO
  static setSSOMode(enabled: boolean) {
    this.useSSO = enabled;
  }

  // Login tradicional com email e senha
  static async login(credentials: LoginCredentials): Promise<any> {

    return await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password
    });
    
    // try {
    //   logger.info('Tentativa de login tradicional:', credentials.email);

    //   // Buscar usuário no Supabase
    //   const { data: userData, error } = await supabase
    //     .from('usuarios')
    //     .select('*')
    //     .eq('email', credentials.email)
    //     .eq('is_active', true)
    //     .single();

    //   if (error) {
    //     logger.error('Erro ao buscar usuário:', error);
        
    //     // Se usuário não encontrado (PGRST116), retornar erro específico
    //     if (error.code === 'PGRST116') {
    //       return { success: false, error: 'Email não cadastrado no sistema' };
    //     }
        
    //     return { success: false, error: 'Erro ao verificar credenciais. Tente novamente.' };
    //   }

    //   if (!userData) {
    //     logger.warn('Usuário não encontrado:', credentials.email);
    //     return { success: false, error: 'Email não cadastrado no sistema' };
    //   }

    //   // Verificar senha (em produção, usar hash)
    //   if (userData.password !== credentials.password) {
    //     logger.warn('Senha incorreta para:', credentials.email);
    //     return { success: false, error: 'Senha incorreta' };
    //   }

    //   // Converter para formato User
    //   const user: User = {
    //     id: userData.id,
    //     email: userData.email,
    //     name: userData.name,
    //     role: userData.role,
    //     createdAt: userData.created_at,
    //     updatedAt: userData.updated_at,
    //     isActive: userData.is_active
    //   };

    //   this.currentUser = user;
    //   localStorage.setItem('currentUser', JSON.stringify(user));
      
    //   logger.success('Login tradicional realizado com sucesso:', user.email);
    //   return { success: true, user };
    // } catch (error) {
    //   logger.error('Erro no login tradicional:', error);
    //   return { success: false, error: 'Erro interno do servidor' };
    // }
  }

  // Login via SSO Microsoft
  static async loginWithMicrosoft(): Promise<{ success: boolean; user?: User; error?: string }> {
    if (!this.msalInstance || !this.graphService) {
      return { success: false, error: 'SSO não configurado' };
    }

    try {
      logger.info('Iniciando login Microsoft...');
      
      // Fazer login via popup
      const loginResponse = await this.msalInstance.loginPopup({
        scopes: ['User.Read', 'User.ReadBasic.All', 'Group.Read.All'],
        prompt: 'select_account',
      });

      logger.success('Login popup concluído');
      logger.debug('Account recebida:', !!loginResponse.account);

      // Definir a conta no GraphService
      this.graphService.setAccount(loginResponse.account);

      logger.info('Obtendo informações do usuário...');
      // Obter informações do usuário
      const userInfo = await this.graphService.getCurrentUser();
      logger.success('Informações do usuário obtidas:', userInfo.displayName);
      
      // Verificar domínio autorizado
      if (!this.isAuthorizedDomain(userInfo.mail)) {
        await this.msalInstance.logoutPopup();
        return { success: false, error: 'Domínio não autorizado' };
      }

      // Obter grupos do usuário
      const userGroups = await this.graphService.getUserGroups(userInfo.id);
      const role = this.graphService.mapAzureGroupToRole(userGroups, AZURE_GROUPS) as 'master' | 'admin' | 'user';

      // Criar usuário no Supabase se não existir
      logger.info('Tentando criar/atualizar usuário no Supabase...');
      const supabaseUser = await this.createOrUpdateSupabaseUser(userInfo, role);
      logger.debug('SupabaseUser retornado:', supabaseUser);

      if (!supabaseUser) {
        logger.error('SupabaseUser é null - tabela usuarios pode não existir');
        throw new Error('Falha ao criar/atualizar usuário no Supabase. Verifique se a tabela "usuarios" existe.');
      }

      // Criar objeto User
      const user: User = {
        id: supabaseUser.id,
        email: userInfo.mail,
        name: userInfo.displayName,
        role: role,
        createdAt: supabaseUser.created_at,
        updatedAt: supabaseUser.updated_at,
        isActive: true
      };

      this.currentUser = user;
      localStorage.setItem('currentUser', JSON.stringify(user));
      
      return { success: true, user };
    } catch (error) {
      logger.error('Erro no login SSO:', error);
      return { success: false, error: 'Erro na autenticação Microsoft' };
    }
  }

  // Verificar se domínio é autorizado
  private static isAuthorizedDomain(email: string): boolean {
    return AUTHORIZED_DOMAINS.some(domain => email.endsWith(domain));
  }

  // Criar ou atualizar usuário no Supabase (com fallback robusto)
  private static async createOrUpdateSupabaseUser(userInfo: any, role: string): Promise<any> {
    try {
      logger.info('Criando/atualizando usuário SSO no Supabase...');
      
      // Verificar se usuário já existe
      const { data: existingUser, error: checkError } = await supabase
        .from('usuarios')
        .select('*')
        .eq('entra_id', userInfo.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        logger.warn('Erro ao verificar usuário existente, tentando criar novo:', checkError.message);
      }

      const userData = {
        email: userInfo.mail,
        name: userInfo.displayName,
        role: role,
        entra_id: userInfo.id,
        is_active: true,
        updated_at: new Date().toISOString()
      };

      let result;
      if (existingUser) {
        // Atualizar usuário existente
        logger.info('Atualizando usuário existente:', userInfo.mail);
        const { data, error } = await supabase
          .from('usuarios')
          .update(userData)
          .eq('entra_id', userInfo.id)
          .select()
          .single();
        
        if (error) {
          logger.warn('Erro ao atualizar usuário, criando fallback local:', error.message);
          result = this.createFallbackUser(userInfo, role);
        } else {
          result = data;
        }
      } else {
        // Criar novo usuário
        logger.info('Criando novo usuário:', userInfo.mail);
        const { data, error } = await supabase
          .from('usuarios')
          .insert({
            ...userData,
            created_at: new Date().toISOString()
          })
          .select()
          .single();
        
        if (error) {
          logger.warn('Erro ao criar usuário no Supabase, criando fallback local:', error.message);
          logger.debug('Detalhes do erro Supabase:', JSON.stringify(error, null, 2));
          result = this.createFallbackUser(userInfo, role);
        } else {
          result = data;
        }
      }

      logger.success('Usuário SSO processado:', result.email, '| Role:', result.role);
      return result;
    } catch (error) {
      logger.error('Erro crítico ao processar usuário SSO, criando fallback:', error);
      return this.createFallbackUser(userInfo, role);
    }
  }

  // Criar usuário local como fallback
  private static createFallbackUser(userInfo: any, role: string): any {
    logger.info('Criando usuário SSO local (fallback)...');
    
    const fallbackUser = {
      id: 'sso-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      email: userInfo.mail,
      name: userInfo.displayName,
      role: role,
      entra_id: userInfo.id,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    logger.success('Usuário SSO local criado:', fallbackUser.email, '| Role:', role);
    return fallbackUser;
  }


  static async logout(): Promise<void> {
    logger.info('Realizando logout...');
    
    // Logout do Microsoft se estiver usando SSO
    if (this.useSSO && this.msalInstance) {
      try {
        await this.msalInstance.logoutPopup();
        logger.success('Logout MSAL realizado');
      } catch (error) {
        logger.error('Erro no logout MSAL:', error);
      }
    }

    this.currentUser = null;
    localStorage.removeItem('currentUser');
    logger.success('Logout concluído');
  }

  static getCurrentUser(): User | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    const stored = localStorage.getItem('currentUser');
    if (stored) {
      this.currentUser = JSON.parse(stored);
      return this.currentUser;
    }

    return null;
  }

  static isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  static hasRole(role: 'master' | 'admin' | 'user'): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    const roleHierarchy = { user: 1, admin: 2, master: 3 };
    return roleHierarchy[user.role] >= roleHierarchy[role];
  }

  static canManageUsers(): boolean {
    return this.hasRole('admin');
  }

  static canAccessMaster(): boolean {
    return this.hasRole('master');
  }

  // Métodos para gerenciamento de usuários (apenas para admin/master)
  static async getAllUsers(): Promise<User[]> {
    if (!this.canManageUsers()) {
      throw new Error('Acesso negado');
    }

    try {
      logger.info('Buscando usuários no Supabase...');
      
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar usuários:', error);
        throw new Error('Erro ao buscar usuários no banco de dados');
      }

      // Converter dados do Supabase para formato User
      const users: User[] = (data || []).map((user: any) => ({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        createdAt: user.created_at,
        updatedAt: user.updated_at,
        isActive: user.is_active
      }));

      logger.success(`${users.length} usuários encontrados`);
      return users;
    } catch (error) {
      logger.error('Erro ao buscar usuários:', error);
      // Retornar array vazio em caso de erro (sem fallback mockado)
      logger.info('Retornando lista vazia devido ao erro');
      return [];
    }
  }

  static async createUser(userData: CreateUserData): Promise<{ success: boolean; user?: User; error?: string }> {
    if (!this.canManageUsers()) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      logger.info('Criando usuário no Supabase:', userData.email);
      
      // Verificar se usuário já existe (com tratamento de erro RLS)
      const { data: existingUser, error: checkError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', userData.email)
        .single();

      // Se erro de RLS (42P17), pular verificação e tentar criar diretamente
      if (checkError && checkError.code === '42P17') {
        logger.warn('Erro de RLS detectado, pulando verificação de usuário existente');
      } else if (checkError && checkError.code !== 'PGRST116') {
        logger.error('Erro ao verificar usuário existente:', checkError);
        return { success: false, error: 'Erro ao verificar usuário existente' };
      } else if (existingUser) {
        return { success: false, error: 'Email já cadastrado' };
      }

      // Gerar senha padrão se não fornecida
      const defaultPassword = userData.password || '123456'; // Em produção, gerar senha aleatória

      // Criar usuário no Supabase
      const { data: newUser, error: createError } = await supabase
        .from('usuarios')
        .insert({
          email: userData.email,
          name: userData.name,
          password: defaultPassword, // Em produção, usar hash
          role: userData.role,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (createError) {
        logger.error('Erro ao criar usuário:', createError);
        
        // Se erro de RLS, criar usuário temporário local
        if (createError.code === '42P17') {
          logger.warn('Erro de RLS detectado, criando usuário temporário local');
          const tempUser: User = {
            id: 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            email: userData.email,
            name: userData.name,
            role: userData.role,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isActive: true
          };
          
          logger.success('Usuário temporário criado:', tempUser.email);
          return { success: true, user: tempUser };
        }
        
        return { success: false, error: 'Erro ao criar usuário no banco de dados' };
      }

      // Converter para formato User
      const user: User = {
        id: newUser.id,
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        createdAt: newUser.created_at,
        updatedAt: newUser.updated_at,
        isActive: newUser.is_active
      };

      logger.success('Usuário criado com sucesso:', user.email);
      return { success: true, user };
    } catch (error) {
      logger.error('Erro ao criar usuário:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }

  static async updateUser(userData: UpdateUserData): Promise<{ success: boolean; user?: User; error?: string }> {
    if (!this.canManageUsers()) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      logger.info('Atualizando usuário no Supabase:', userData.id);
      
      // Verificar se usuário existe
      const { error: checkError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('id', userData.id)
        .single();

      if (checkError) {
        logger.error('Erro ao verificar usuário existente:', checkError);
        return { success: false, error: 'Usuário não encontrado' };
      }

      // Preparar dados para atualização
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (userData.email) updateData.email = userData.email;
      if (userData.name) updateData.name = userData.name;
      if (userData.role) updateData.role = userData.role;
      if (userData.isActive !== undefined) updateData.is_active = userData.isActive;

      // Atualizar usuário no Supabase
      const { data: updatedUser, error: updateError } = await supabase
        .from('usuarios')
        .update(updateData)
        .eq('id', userData.id)
        .select()
        .single();

      if (updateError) {
        logger.error('Erro ao atualizar usuário:', updateError);
        return { success: false, error: 'Erro ao atualizar usuário no banco de dados' };
      }

      // Converter para formato User
      const user: User = {
        id: updatedUser.id,
        email: updatedUser.email,
        name: updatedUser.name,
        role: updatedUser.role,
        createdAt: updatedUser.created_at,
        updatedAt: updatedUser.updated_at,
        isActive: updatedUser.is_active
      };

      logger.success('Usuário atualizado com sucesso:', user.email);
      return { success: true, user };
    } catch (error) {
      logger.error('Erro ao atualizar usuário:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }

  static async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    if (!this.canManageUsers()) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      logger.info('Excluindo usuário do Supabase:', userId);
      
      // Verificar se usuário existe
      const { data: existingUser, error: checkError } = await supabase
        .from('usuarios')
        .select('id, role')
        .eq('id', userId)
        .single();

      if (checkError) {
        logger.error('Erro ao verificar usuário existente:', checkError);
        return { success: false, error: 'Usuário não encontrado' };
      }

      // Verificar se não é um usuário master
      if (existingUser.role === 'master') {
        return { success: false, error: 'Não é possível excluir usuário master' };
      }

      // Excluir usuário do Supabase
      const { error: deleteError } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', userId);

      if (deleteError) {
        logger.error('Erro ao excluir usuário:', deleteError);
        return { success: false, error: 'Erro ao excluir usuário do banco de dados' };
      }

      logger.success('Usuário excluído com sucesso:', userId);
      return { success: true };
    } catch (error) {
      logger.error('Erro ao excluir usuário:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }

  static async changePassword(passwordData: ChangePasswordData): Promise<{ success: boolean; error?: string }> {
    const user: any = this.getCurrentUser();
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    try {
      // Verificar se as novas senhas coincidem
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        return { success: false, error: 'As senhas não coincidem' };
      }

      // Se usuário tem entra_id, é usuário SSO - senha gerenciada pelo Azure AD
      if (user.entra_id) {
        logger.info('Usuário SSO - senha gerenciada pelo Azure AD');
        return { 
          success: false, 
          error: 'Para alterar sua senha, acesse o portal do Microsoft 365 ou Azure AD. A senha é gerenciada centralmente pelo sistema de autenticação.' 
        };
      }

      // Usuário tradicional - atualizar senha no Supabase
      const { error } = await supabase
        .from('usuarios')
        .update({ 
          password: passwordData.newPassword, // Em produção, usar hash
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        logger.error('Erro ao atualizar senha:', error);
        return { success: false, error: 'Erro ao atualizar senha' };
      }

      logger.success('Senha alterada com sucesso para:', user.email);
      return { success: true };
    } catch (error) {
      logger.error('Erro ao alterar senha:', error);
      return { success: false, error: 'Erro interno do servidor' };
    }
  }
}
